/**
 * E-commerce události pro Google Tag Manager (GA4 formát) a souhlas z cookie lišty Shopify.
 * Načítá se jen s vyplněným ID kontejneru (snippets/bestio-analytics.liquid). Hodnoty bere z Liquidu, /cart.js a z dat,
 * která checkout už má; autoritativní cenu nepočítá (hodnota změny košíku je jen součet cen řádků z /cart.js) a neposílá
 * žádné údaje zákazníka. Chyba měření nikdy nezastaví košík ani checkout.
 *
 * - view_item: detail produktu (data z Liquidu v window.bestioAnalyticsProduct),
 * - view_item_list: výpis kolekce, výsledky vyhledávání a police produktů (položky z Liquidu v atributech
 *   snippets/bestio-analytics-list.liquid a bestio-analytics-item.liquid); jednou za vykreslenou položku, takže nová sada
 *   výsledků z filtrů (facets.js) nebo další stránka („Načíst další“) se pošle znovu jen se svými položkami,
 * - select_item: klik na odkaz produktu v takovém výpisu,
 * - view_cart: stránka košíku a otevření košíkového šuplíku (`bestio:cart-drawer:open` z cart-drawer.js), když košík není prázdný,
 * - add_to_cart a remove_from_cart: rozdíl počtu kusů podle varianty proti poslednímu známému stavu /cart.js, po
 *   `cart:add-confirmation:open` a po `maximize:cart:updated` (změna počtu nebo odebrání v košíku a šuplíku); srovnání košíku
 *   z jiné záložky nebo po objednávce (`source: 'bestio-cart-sync'`) jen posune výchozí stav; přidání tlačítkem položky
 *   výpisu (alternativy u detailu) nese u té položky item_list_id, item_list_name a pořadí,
 * - begin_checkout: první stránka našeho checkoutu, jednou za košík; hodnota a položky platí i pro další kroky,
 * - add_shipping_info a add_payment_info: `bestio:checkout:selection-confirmed` z assets/bestio-checkout.js, když zákazník
 *   potvrdí krok dopravy a platby; jednou za košík a krok, hodnota a položky z begin_checkout,
 * - purchase: `bestio:checkout:thank-you` z assets/bestio-checkout.js, až objednávka existuje a není zrušená ani vrácená;
 *   jednou na objednávku a prohlížeč. Částky, měna, položky a příznak testu jsou z Checkout API podle objednávky v Shopify
 *   (`purchase`), v každém prohlížeči stejné: hodnota je zboží bez DPH po všech slevách, bez dopravy a dobírky (rozhodnutí
 *   vlastníka 18. 10. 2026), doprava s dobírkou v `shipping`, DPH v `tax`. Návrat z brány v jiném prohlížeči nese jen
 *   částky bez položek (BEST-504). Bez serverového nákupu a u testovací objednávky se neposílá nic.
 *
 * Každý objekt ecommerce nese `market` (cz, sk) z window.bestioAnalyticsMarket; GTM ho posílá jako parametr události.
 */
(() => {
  window.dataLayer = window.dataLayer || [];
  const gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  const market = typeof window.bestioAnalyticsMarket === 'string' && window.bestioAnalyticsMarket ? window.bestioAnalyticsMarket : undefined;
  const push = (event, ecommerce) => {
    window.dataLayer.push({ecommerce: null});
    window.dataLayer.push({event, ecommerce: market ? {...ecommerce, market} : ecommerce});
  };
  const storage = (kind, key, value) => {
    try {
      const store = window[kind];
      if (value === undefined) return store.getItem(key);
      store.setItem(key, value);
    } catch { /* soukromé okno nebo zakázané úložiště: událost se pošle bez něj */ }
    return null;
  };
  const money = amount => Math.round(amount * 100) / 100;

  // Souhlas: cookie lišta Shopify (Customer Privacy API) řídí analytiku i reklamu.
  const updateConsent = () => {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy) return;
    const analytics = privacy.analyticsProcessingAllowed() ? 'granted' : 'denied';
    const marketing = privacy.marketingAllowed() ? 'granted' : 'denied';
    gtag('consent', 'update', {
      analytics_storage: analytics, ad_storage: marketing, ad_user_data: marketing, ad_personalization: marketing,
    });
  };
  // API bývá načtené už obsahem content_for_header; callback loadFeatures přijde až po chvíli.
  if (window.Shopify?.customerPrivacy?.currentVisitorConsent) updateConsent();
  window.Shopify?.loadFeatures?.([{name: 'consent-tracking-api', version: '0.1'}], error => { if (!error) updateConsent(); });
  document.addEventListener('visitorConsentCollected', updateConsent);

  const root = window.Shopify?.routes?.root || '/';
  const getCart = () => fetch(`${root}cart.js`, {headers: {Accept: 'application/json'}}).then(response => response.json());
  // `item_id` varianty: SKU, bez něj číslo varianty (i z GID). Stejný klíč jako `g:id` produktového feedu
  // (ComparisonIdentifiers.VariantKey v bestio-hub), takže GA4, Ads a Merchant Center položky spárují.
  const itemId = (sku, variantId) => (String(sku ?? '').trim() ? String(sku) : String(variantId ?? '').replace(/\?.*$/, '').split('/').pop());
  const toItem = line => ({
    item_id: itemId(line.sku, line.variant_id),
    item_name: line.product_title,
    item_brand: line.vendor,
    item_category: line.product_type || undefined,
    item_variant: line.variant_title || undefined,
    price: line.final_price / 100,
    quantity: line.quantity,
  });
  const cartEvent = cart => ({currency: cart.currency, value: cart.total_price / 100, items: cart.items.map(toItem)});

  const product = window.bestioAnalyticsProduct;
  if (product) push('view_item', {currency: product.currency, value: product.item.price, items: [product.item]});

  // --- Výpisy produktů -------------------------------------------------------------------------------------------
  const readJson = (element, attribute) => {
    try { return JSON.parse(element.getAttribute(attribute) || 'null'); } catch { return null; }
  };
  const listOf = container => {
    const list = readJson(container, 'data-bestio-analytics-list');
    return list?.item_list_id ? list : null;
  };
  const listItem = (card, list) => {
    const item = readJson(card, 'data-bestio-analytics-item');
    if (!item?.item_id) return null;
    if (!item.item_category) delete item.item_category;
    return {...item, item_list_id: list.item_list_id, item_list_name: list.item_list_name};
  };
  // Položka se ohlásí jednou za vykreslení: filtry nahradí výpis novými prvky, „Načíst další“ jen přidá další.
  const shownCards = new WeakSet();
  const reportLists = () => {
    try {
      document.querySelectorAll('[data-bestio-analytics-list]').forEach(container => {
        const list = listOf(container);
        if (!list) return;
        const items = [];
        container.querySelectorAll('[data-bestio-analytics-item]').forEach(card => {
          if (shownCards.has(card)) return;
          shownCards.add(card);
          const item = listItem(card, list);
          if (item) items.push(item);
        });
        if (items.length) push('view_item_list', {item_list_id: list.item_list_id, item_list_name: list.item_list_name, currency: list.currency, items});
      });
    } catch { /* měření nesmí rozbít výpis */ }
  };
  if (document.querySelector('[data-bestio-analytics-list]')) {
    reportLists();
    let listTimer = null;
    // Filtry i „Načíst další“ mění jen obsah kontejneru seznamu; ten sám zůstává, proto se sleduje jen on.
    const observer = new MutationObserver(() => {
      if (listTimer) return;
      listTimer = setTimeout(() => { listTimer = null; reportLists(); }, 250);
    });
    document.querySelectorAll('[data-bestio-analytics-list]').forEach(container => observer.observe(container, {childList: true, subtree: true}));
  }
  // Přidání do košíku tlačítkem položky výpisu (alternativy u detailu, BEST-627): add_to_cart té položky nese její seznam.
  // Pamatuje se jen poslední takové tlačítko a spáruje se s přidanou položkou stejného item_id; každé přidání ho spotřebuje.
  let listedAdd = null;
  document.addEventListener('click', event => {
    try {
      const button = event.target?.closest?.('button[name="add"]');
      const card = button?.closest('[data-bestio-analytics-item]');
      const container = card?.closest('[data-bestio-analytics-list]');
      const list = container && listOf(container);
      const item = list && listItem(card, list);
      if (item) listedAdd = item;
    } catch { /* měření nesmí zastavit přidání do košíku */ }
  }, true);
  document.addEventListener('click', event => {
    try {
      const link = event.target?.closest?.('a[href]');
      const card = link?.closest('[data-bestio-analytics-item]');
      const container = card?.closest('[data-bestio-analytics-list]');
      if (!container || !/\/products\//.test(link.getAttribute('href') || '')) return;
      const list = listOf(container);
      const item = list && listItem(card, list);
      if (item) push('select_item', {item_list_id: list.item_list_id, item_list_name: list.item_list_name, currency: list.currency, items: [item]});
    } catch { /* měření nesmí zastavit přechod na produkt */ }
  }, true);

  // --- Košík -----------------------------------------------------------------------------------------------------
  // Poslední známý stav košíku: počet kusů a řádek podle varianty. Změny se zpracují jedna po druhé ve frontě, takže
  // přidání ohlášené dvěma událostmi (potvrzení přidání a aktualizace košíku) se započítá jednou.
  let known = null;
  const remember = cart => {
    known = new Map();
    cart.items.forEach(line => {
      const variantId = String(line.variant_id);
      known.set(variantId, {line: known.get(variantId)?.line || line, quantity: (known.get(variantId)?.quantity || 0) + line.quantity});
    });
  };
  const changesSince = (before, cart) => {
    remember(cart);
    const added = [];
    const removed = [];
    if (!before) return {added, removed};
    known.forEach(({line, quantity}, variantId) => {
      const delta = quantity - (before.get(variantId)?.quantity || 0);
      if (delta > 0) added.push({variantId, item: {...toItem(line), quantity: delta}});
    });
    before.forEach(({line, quantity}, variantId) => {
      const delta = quantity - (known.get(variantId)?.quantity || 0);
      if (delta > 0) removed.push({variantId, item: {...toItem(line), quantity: delta}});
    });
    return {added, removed};
  };
  const pushLines = (event, currency, changes) => {
    if (!changes.length) return;
    let items = changes.map(change => change.item);
    if (event === 'add_to_cart') {
      const listed = listedAdd;
      listedAdd = null;
      if (listed) {
        items = items.map(item => (item.item_id === listed.item_id
          ? {...item, item_list_id: listed.item_list_id, item_list_name: listed.item_list_name, index: listed.index}
          : item));
      }
    }
    push(event, {currency, value: money(items.reduce((sum, item) => sum + item.price * item.quantity, 0)), items});
  };
  const isCartPage = Boolean(window.Maximize?.isCartPage);
  let cartQueue = getCart().then(cart => {
    remember(cart);
    if (isCartPage && cart.items.length) push('view_cart', cartEvent(cart));
  }).catch(() => {});
  const enqueue = work => { cartQueue = cartQueue.then(work).catch(() => {}); };

  document.addEventListener('cart:add-confirmation:open', event => {
    const variantId = String(event.detail?.variantId || '');
    enqueue(async () => {
      const before = known;
      const cart = await getCart();
      const {added, removed} = changesSince(before, cart);
      // Výchozí stav načtený až po přidání (nebo nenačtený) rozdíl nepozná; přidání se pak počítá jako jeden kus.
      if (variantId && !added.some(change => change.variantId === variantId)) {
        const line = cart.items.find(item => String(item.variant_id) === variantId);
        if (line) added.push({variantId, item: {...toItem(line), quantity: 1}});
      }
      pushLines('add_to_cart', cart.currency, added);
      pushLines('remove_from_cart', cart.currency, removed);
    });
  });
  // CART_EVENT.cartUpdate z assets/constants.js.
  document.addEventListener('maximize:cart:updated', event => {
    const synced = event.detail?.source === 'bestio-cart-sync';
    enqueue(async () => {
      const before = known;
      const cart = await getCart();
      if (synced) { remember(cart); return; }
      const {added, removed} = changesSince(before, cart);
      pushLines('add_to_cart', cart.currency, added);
      pushLines('remove_from_cart', cart.currency, removed);
    });
  });
  // Stránka z mezipaměti zpět/vpřed nebo záložka po návratu: košík se mohl změnit jinde, jen se posune výchozí stav.
  const resync = () => enqueue(async () => { remember(await getCart()); });
  window.addEventListener('pageshow', event => { if (event.persisted) resync(); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') resync(); });
  document.addEventListener('bestio:cart-drawer:open', () => {
    enqueue(async () => {
      const cart = await getCart();
      if (cart.items.length) push('view_cart', cartEvent(cart));
    });
  });

  // --- Checkout --------------------------------------------------------------------------------------------------
  // Kroky checkoutu ohlášené pro košík, oddělené čárkou ('1' je záznam z doby, kdy se ukládal jen begin_checkout).
  const stepsKey = token => `bestio_analytics_begin_${token}`;
  const sentSteps = token => {
    const raw = storage('sessionStorage', stepsKey(token)) || '';
    return raw === '1' ? ['begin_checkout'] : raw.split(',').filter(Boolean);
  };
  const sendOnce = (token, event, ecommerce) => {
    const sent = sentSteps(token);
    if (sent.includes(event)) return;
    storage('sessionStorage', stepsKey(token), [...sent, event].join(','));
    push(event, ecommerce);
  };
  const path = window.location.pathname;
  let checkoutReady = Promise.resolve(null);
  if (/\/pages\/checkout-/.test(path) && !/thank-you/.test(path)) {
    checkoutReady = getCart().then(cart => {
      if (!cart.items.length) return null;
      const checkout = cartEvent(cart);
      sendOnce(cart.token, 'begin_checkout', checkout);
      return {token: cart.token, checkout};
    }).catch(() => null);
  }

  document.addEventListener('bestio:checkout:selection-confirmed', event => {
    const {deliveryMethodCode, paymentMethodCode} = event.detail || {};
    checkoutReady.then(ready => {
      if (!ready) return;
      if (deliveryMethodCode) sendOnce(ready.token, 'add_shipping_info', {...ready.checkout, shipping_tier: String(deliveryMethodCode)});
      if (paymentMethodCode) sendOnce(ready.token, 'add_payment_info', {...ready.checkout, payment_type: String(paymentMethodCode)});
    }).catch(() => {});
  });

  const amountOf = raw => (Number.isFinite(Number(raw)) ? Number(raw) : 0);
  const variantNumber = id => String(id ?? '').replace(/\?.*$/, '').split('/').pop();
  /**
   * Řádek objednávky z Checkout API jako položka GA4: cena za kus bez DPH po všech slevách (i slevě na celou objednávku),
   * takže součet cena × kusy je hodnota nákupu. Názvy jen ze shrnutí záložky (`names`), jsou-li.
   */
  const orderItem = (line, names) => {
    const quantity = Math.max(1, Math.round(amountOf(line?.quantity)) || 1);
    const named = names.get(variantNumber(line?.variantId)) || {};
    return {
      item_id: itemId(line?.sku, line?.variantId),
      item_name: named.name || undefined,
      item_brand: named.vendor || undefined,
      item_category: named.productType || undefined,
      item_variant: named.variantTitle || undefined,
      // V haléřích/centech, ať 358,20 − 62,17 nevyjde o desetinu haléře jinak.
      price: Math.round((Math.round(amountOf(line?.amount) * 100) - Math.round(amountOf(line?.taxAmount) * 100)) / quantity) / 100,
      quantity,
    };
  };
  /**
   * Nákup podle objednávky v Shopify (`purchase` z Checkout API): hodnota je zboží bez DPH po všech slevách, bez dopravy
   * a dobírky (rozhodnutí vlastníka 18. 10. 2026; u přenesené daňové povinnosti je zboží bez DPH už v ceně). Doprava
   * s dobírkou, jak byla účtována, jde v `shipping`, DPH objednávky v `tax`. Shrnutí záložky (`order`) přidá jen názvy
   * a kódy slev.
   */
  const purchaseOf = (transactionId, purchase, order) => {
    const names = new Map((Array.isArray(order?.lines) ? order.lines : []).filter(Boolean)
      .map(line => [variantNumber(line.variantId), line]));
    const lines = (Array.isArray(purchase.lines) ? purchase.lines : []).filter(line => line && line.variantId && amountOf(line.quantity) > 0);
    const event = {
      transaction_id: transactionId,
      currency: String(purchase.currencyCode),
      value: Math.max(0, Math.round(amountOf(purchase.goodsAmount) * 100) - Math.round(amountOf(purchase.goodsTaxAmount) * 100)) / 100,
      shipping: money(amountOf(purchase.shippingAmount)),
      tax: money(amountOf(purchase.taxAmount)),
      items: lines.map(line => orderItem(line, names)),
    };
    const coupon = (Array.isArray(order?.discountCodes) ? order.discountCodes : []).map(code => String(code ?? '').trim()).filter(Boolean).join(',');
    if (coupon) event.coupon = coupon;
    return event;
  };
  // Souhlas s analytikou nebo marketingem podle cookie lišty Shopify (stejné pravidlo jako nárok nákupu v pokladně).
  const consentGiven = () => {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return privacy?.analyticsProcessingAllowed?.() === true || privacy?.marketingAllowed?.() === true;
    } catch { return false; }
  };
  // Ohlášené nákupy této stránky: děkovací stránka se při čekání na platbu překresluje opakovaně a úložiště nemusí jít
  // zapsat (soukromé okno, zakázané úložiště), nákup se ale z jedné stránky ohlásí nejvýš jednou.
  const purchased = new Set();
  document.addEventListener('bestio:checkout:thank-you', event => {
    const {orderId, status, cancelled, refunded, purchase, purchaseClaimed, order} = event.detail || {};
    // Zrušená objednávka nákupem není (BEST-524), objednávka s vrácenými penězi také ne.
    if (!orderId || status === 'PaymentFailed' || cancelled || refunded) return;
    // Bez nákupu ze serveru (Checkout API objednávku nepřečetlo) se neposílá nic, ani jiná částka: další vykreslení nebo
    // návrat na stránku ho může přinést. Testovací objednávku (štítek bestio-test, testovací platba nebo testovací produkt,
    // podle Shopify) server označí a neměří se.
    if (!purchase || typeof purchase !== 'object' || !purchase.currencyCode || purchase.test) return;
    // Another page (another browser or tab) was handed this purchase first: it measures it, this one does not. Null is a
    // claim the server could not decide: the checkout reads it again, nothing is sent now.
    if (purchaseClaimed === false || purchaseClaimed === null) return;
    const transactionId = String(orderId).split('/').pop();
    const purchaseKey = `bestio_analytics_purchase_${transactionId}`;
    // Once per order and browser, with or without the visitor's consent (decision of the owner 19. 10. 2026: a purchase sent
    // without consent is not sent again when the consent comes). Kept as `consent` or `anon` (older pages kept `1`).
    if (purchased.has(transactionId) || storage('localStorage', purchaseKey)) return;
    purchased.add(transactionId);
    storage('localStorage', purchaseKey, consentGiven() ? 'consent' : 'anon');
    push('purchase', purchaseOf(transactionId, purchase, order));
  });
})();
